import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Download, Search, FileText, Receipt, CheckCircle2, XCircle } from 'lucide-react';
import { api, API_BASE_URL, tokenStore } from '@/api/client';
import { toCommaList } from '@/utils/search-list';

/**
 * Bulk B2B documents (operator, 28-10): two tabs — **Waybill copy** and **Invoices**. Paste up to
 * 100 LR numbers, check them, and download ONE ZIP holding one PDF per LR (named by the LR). The
 * documents are the same ones the order screen prints for a single consignment. Gated like Bulk POD.
 */

const MAX = 100;
type Kind = 'waybill' | 'invoice';
type Status = 'READY' | 'NOT_FOUND' | 'NOT_B2B' | 'CANCELLED' | 'NO_LR';
interface Row { number: string; status: Status; orderNo?: string; lrNo?: string | null; merchantName?: string | null; customerName?: string | null; statusName?: string | null; message?: string }

const STATUS_STYLE: Record<Status, string> = {
  READY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  NOT_FOUND: 'bg-slate-100 text-slate-600 border-slate-200',
  NOT_B2B: 'bg-amber-50 text-amber-700 border-amber-200',
  CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
  NO_LR: 'bg-amber-50 text-amber-700 border-amber-200',
};
const STATUS_LABEL: Record<Status, string> = { READY: 'Ready', NOT_FOUND: 'Not found', NOT_B2B: 'Not B2B', CANCELLED: 'Cancelled', NO_LR: 'No LR yet' };
const TABS: Array<{ id: Kind; label: string; icon: JSX.Element; what: string }> = [
  { id: 'waybill', label: 'Waybill copy', icon: <FileText size={15} />, what: 'waybill copy (3 pages — shipper, LM POD, recipient)' },
  { id: 'invoice', label: 'Invoices', icon: <Receipt size={15} />, what: 'invoice' },
];

export function BulkB2bDocsPage() {
  const [kind, setKind] = useState<Kind>('waybill');
  const [text, setText] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [zipping, setZipping] = useState(false);

  const numbers = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const piece of text.split(/[\s,;]+/)) {
      const v = piece.trim();
      if (!v || seen.has(v.toUpperCase())) continue;
      seen.add(v.toUpperCase());
      out.push(v);
    }
    return out;
  }, [text]);
  const overLimit = numbers.length > MAX;
  const tab = TABS.find((t) => t.id === kind)!;

  const lookup = useMutation({
    mutationFn: async () => (await api.post('/b2b-docs/lookup', { numbers: text })).data as { rows: Row[]; ready: number },
    onSuccess: (r) => setRows(r.rows),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Lookup failed'),
  });
  const readyCount = (rows ?? []).filter((r) => r.status === 'READY').length;

  const downloadZip = async () => {
    if (!numbers.length) { toast.error('Enter at least one LR number'); return; }
    if (overLimit) { toast.error(`Maximum ${MAX} LR numbers at a time`); return; }
    setZipping(true);
    try {
      // A POST with the auth header, so a blob download rather than a plain link.
      const res = await fetch(`${API_BASE_URL}/b2b-docs/zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenStore.get()}` },
        body: JSON.stringify({ kind, numbers: text }),
      });
      if (!res.ok) {
        const t = await res.text();
        let msg = `HTTP ${res.status}`;
        try { msg = JSON.parse(t)?.message ?? msg; } catch { msg = t.slice(0, 200) || msg; }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `b2b-${kind === 'waybill' ? 'waybill-copies' : 'invoices'}-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success(`ZIP downloaded — one PDF per LR. Anything left out is listed in _NOT_INCLUDED.txt inside.`);
    } catch (e: any) {
      toast.error(e?.message ?? 'ZIP download failed');
    } finally { setZipping(false); }
  };

  return (
    <div className="p-6 max-w-[1100px] mx-auto" data-testid="b2b-docs">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileText size={22} /> Bulk Waybill &amp; Invoices (B2B)</h1>
        <p className="text-sm text-slate-500 mt-0.5">Enter up to {MAX} LR numbers and download every consignment's {tab.what} as one ZIP — one PDF per LR.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setKind(t.id)} data-testid={`b2b-docs-tab-${t.id}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 flex items-center gap-1.5 ${kind === t.id ? 'border-primary text-primary font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <label className="block text-xs font-medium text-slate-600">LR numbers (comma, space or one per line)</label>
        <textarea value={text} rows={5} data-testid="b2b-docs-input"
          onChange={(e) => { setText(toCommaList(e.target.value)); setRows(null); }}
          placeholder="320152638, 320054372, 320212383"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-xs ${overLimit ? 'text-rose-600 font-medium' : 'text-slate-500'}`}>{numbers.length} / {MAX} LR{numbers.length === 1 ? '' : 's'}{overLimit ? ' — too many, remove some' : ''}</span>
          <button onClick={() => lookup.mutate()} disabled={!numbers.length || overLimit || lookup.isPending} data-testid="b2b-docs-check"
            className="ml-auto px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50">
            {lookup.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check LRs
          </button>
          <button onClick={downloadZip} disabled={!numbers.length || overLimit || zipping || (rows !== null && readyCount === 0)} data-testid="b2b-docs-zip"
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
            {zipping ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download {tab.label} ZIP{rows ? ` (${readyCount})` : ''}
          </button>
        </div>
        {zipping && <p className="text-[11px] text-slate-500">Building the PDFs — about a second per LR, so 100 LRs take a minute or two. Keep this tab open.</p>}
      </div>

      {rows && (
        <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100 flex items-center gap-3">
            <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> {readyCount} ready</span>
            <span className="flex items-center gap-1 text-slate-500"><XCircle size={13} /> {rows.length - readyCount} will be left out</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr><th className="text-left px-3 py-2">LR</th><th className="text-left px-3 py-2">Order</th><th className="text-left px-3 py-2">Merchant</th><th className="text-left px-3 py-2">Consignee</th><th className="text-left px-3 py-2">Shipment status</th><th className="text-left px-3 py-2">Result</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.number} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{r.lrNo ?? r.number}</td>
                  <td className="px-3 py-2 text-xs">{r.orderNo ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.merchantName ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.customerName ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.statusName ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`inline-block border rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                    {r.status !== 'READY' && r.message && <span className="ml-2 text-slate-500">{r.message}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
